import { useEffect, useState } from 'react';
import { GRID_ROWS } from '@/pages/spatial-span-shinhan/gameConfig';

export interface ViewportMetrics {
  height: number;
  tileSize: number;
  boardPadding: number;
  guidanceHeight: number;
  sequenceHeight: number;
  screenPt: number;
  screenPb: number;
  sequenceMt: number;
}

// 신한 SOL 버전 상단 네비게이션 바 높이 (뒤로가기/홈)
const TOP_BAR_HEIGHT = 56;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// 실제 사용 가능한 viewport 높이를 읽어 게임 영역 레이아웃 지표를 계산한다.
// 브라우저/WebView 주소창·탭바 등 UI 변화는 visualViewport.height 로 감지한다.
function computeMetrics(): ViewportMetrics {
  const visualViewport =
    typeof window !== 'undefined' ? window.visualViewport : undefined;
  const height = Math.round(
    visualViewport?.height ??
      (typeof window !== 'undefined' ? window.innerHeight : 800),
  );
  const width = typeof window !== 'undefined' ? window.innerWidth : 390;

  // 화면 높이에 따라 상하 여백 / 고정 영역을 우선 축소
  let screenPt: number;
  let screenPb: number;
  let guidanceHeight: number;
  let sequenceHeight: number;
  let sequenceMt: number;
  let boardPadding: number;
  let stageHeight: number;

  // 안내문구 영역을 더 확보하고 게임보드 높이를 줄이기 위해 guidanceHeight 를 키움
  if (height >= 780) {
    screenPt = 16;
    screenPb = 14;
    guidanceHeight = 76;
    sequenceHeight = 40;
    sequenceMt = 8;
    boardPadding = 20;
    stageHeight = 44;
  } else if (height >= 660) {
    screenPt = 12;
    screenPb = 10;
    guidanceHeight = 68;
    sequenceHeight = 36;
    sequenceMt = 6;
    boardPadding = 14;
    stageHeight = 44;
  } else if (height >= 560) {
    screenPt = 8;
    screenPb = 8;
    guidanceHeight = 58;
    sequenceHeight = 32;
    sequenceMt = 4;
    boardPadding = 8;
    stageHeight = 44;
  } else {
    screenPt = 6;
    screenPb = 6;
    guidanceHeight = 52;
    sequenceHeight = 30;
    sequenceMt = 4;
    boardPadding = 6;
    stageHeight = 44;
  }

  // 고정 소비 높이: 상단 바(흰색) + 하단 여백 + 단계 박스(44) + 안내
  // (상단 단계 박스는 배경 레이어 위에 얹힌 전용 레이어라 별도 높이로 계산)
  const fixed =
    TOP_BAR_HEIGHT +
    screenPb +
    stageHeight +
    guidanceHeight;
  const boardHeight = height - fixed;
  const maxTileByHeight = Math.floor(
    (boardHeight - boardPadding * 2) / GRID_ROWS,
  );

  // 너비 기반 타일 크기 (살짝 더 크게: 18vw, 64~78px)
  const widthTile = clamp(Math.round(width * 0.18), 64, 78);
  // 높이가 부족할 때만 타일을 최후 수단으로 축소 (최소 44px)
  const tileSize = clamp(Math.min(widthTile, maxTileByHeight), 44, 78);

  return {
    height,
    tileSize,
    boardPadding,
    guidanceHeight,
    sequenceHeight,
    screenPt,
    screenPb,
    sequenceMt,
  };
}

// CSS 변수를 문서 루트에 주입해 하위 컴포넌트가 var(--*)로 소비할 수 있게 한다.
function applyMetrics(metrics: ViewportMetrics): void {
  const root = document.documentElement.style;
  root.setProperty('--sh-tile-size', `${metrics.tileSize}px`);
  root.setProperty('--sh-board-padding', `${metrics.boardPadding}px`);
  root.setProperty('--sh-guidance-height', `${metrics.guidanceHeight}px`);
  root.setProperty('--sh-sequence-height', `${metrics.sequenceHeight}px`);
  root.setProperty('--sh-screen-pt', `${metrics.screenPt}px`);
  root.setProperty('--sh-screen-pb', `${metrics.screenPb}px`);
  root.setProperty('--sh-sequence-mt', `${metrics.sequenceMt}px`);
}

export function useViewportMetrics(): ViewportMetrics {
  const [metrics, setMetrics] = useState<ViewportMetrics>(() => computeMetrics());

  useEffect(() => {
    const visualViewport = window.visualViewport;
    const update = () => {
      const next = computeMetrics();
      setMetrics(next);
      applyMetrics(next);
    };

    update();

    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return metrics;
}